/**
 * 多版本菜单 B6（老扫码站 U-B6-3）：地址里带着预览码时，每个请求怎么发（请求拦截器 `utils/request/requestMixin.js` 只问这里）。
 *
 * - **带 `X-Preview-Menu` 头**：所有 `/customer/` 下的请求（读菜单 + 下单 + 其它），值是当前地址里的码。
 *   读菜单靠它拿到那份预览菜单（后端 `MenuPreviewTokenService::tokenFromRequest` 头优先）；单店下单靠它被后端拒（D80：形状合法的头一律拒，
 *   纵深 —— 页面上的本地拦单漏了也下不成真单）。读菜单照 U-B6-1 继续带 menuId：后端「显式 menuId > 预览码」，
 *   而这个 menuId 就是本次带着码的 get-menu 解析出来的那份草稿，后端凭同一个码认它（`MenuResolutionService::explicitMenu`）。
 * - **合单（owner D98①）**：合单页 / 合单成员店的**读菜单**不带头 —— 合单成员店一律按主菜单（与今天一样），也不会因为码不是这家店的
 *   而误报「预览已结束」。合单页的下单 / 付款照样在本地拦（下面）。
 * - **不让缓存碰**：带头的请求一律 `cache: false`（`cacheRequest.js` 的 axios-cache-interceptor 0.10.2 认这个开关：不查、不存）——
 *   预览的回包绝不进 5 秒内存缓存、绝不会被同一页的非预览请求拿到；非预览时缓存过的回包也不会被预览请求拿到。
 * - **本地直接拦、不发出去**：后端**没有**预览闸的写接口（G7：pay-order / update-tab / update-order 都不看预览码；
 *   合单下单只在表单里拒、没有 menuPreviewOrdering 码，G4）以及会开出付款的接口（Stripe 付款意向、Windcave 卡页会话）。
 *   这是**第二道**：第一道在页面上（`previewOrderBlock.js`）—— 三个结账按钮拦、付款页与卡页一进来就停。付款页的 Apple Pay、卡页的
 *   Windcave AJAX 提交用的是全局 axios，这一层看不见，只能靠页面那一道；这里兜住页面漏掉的入口（挂账页的快捷付款、开挂账页的预授权等）。
 *   单店 `add-order` 不在这里：它照样带头发出去，后端有闸（D80）。
 */
import { PREVIEW_MENU_HEADER } from './previewMenu'

const CUSTOMER_PREFIX = '/customer/'

/** 读菜单的接口（合单时不带预览头） */
export const PREVIEW_MENU_READ_URLS = [
  '/customer/default/get-menu',
  '/customer/default/get-product-list',
  '/customer/default/get-product',
  '/customer/default/get-recommendations',
  '/customer/default/other-action'
]

/** 预览时本地直接拦下、不发出去的写接口 */
export const PREVIEW_LOCAL_BLOCK_URLS = [
  '/customer/default/pay-order',
  '/customer/default/update-tab',
  '/customer/default/update-order',
  '/customer/default/add-venue-sub-group-order',
  '/customer/default/pay-venue-sub-group-order',
  '/customer/default/create-stripe-payment-intent',
  // U-B6-2 review 第 2 轮 G11：卡页（views/card-detail）Windcave AJAX 模式先取会话、再用全局 axios 把卡号直接交给 Windcave；
  // 后端今天已经没有这个接口（死路），这里是纵深：会话取不到，卡号就到不了网关
  '/customer/default/get-windcave-session'
]

function pathOf(url) {
  if (typeof url !== 'string') return ''
  // 绝对地址（极少）只看路径部分
  return url.split('?')[0].replace(/^https?:\/\/[^/]+/, '')
}

/**
 * @param {Object} input
 * @param {String} input.url 请求的 url（`config.url`）
 * @param {String|null} input.token 当前地址里的预览码（`currentPreviewMenuToken`）
 * @param {Boolean} [input.subGroup] 是不是合单（`store.state.venueHome.subGroupId` 或 `/vgs/` 页面）
 * @returns {{block: Boolean, header: Boolean}} block = 本地拦下不发；header = 带 X-Preview-Menu 并绕开缓存
 */
export function previewRequestPlan({ url, token, subGroup = false } = {}) {
  const path = pathOf(url)
  if (!token || path.indexOf(CUSTOMER_PREFIX) !== 0) return { block: false, header: false }
  if (PREVIEW_LOCAL_BLOCK_URLS.includes(path)) return { block: true, header: false }
  if (subGroup && PREVIEW_MENU_READ_URLS.includes(path)) return { block: false, header: false }
  return { block: false, header: true }
}

/** 给请求配置加上预览头并关掉缓存（不改别的） */
export function applyPreviewHeader(config, token) {
  config.headers = config.headers || {}
  config.headers[PREVIEW_MENU_HEADER] = token
  config.cache = false
  return config
}

/** 请求拦截器抛出去的「预览时不能做」标记（响应错误拦截器认它，不当成网络错误） */
export function previewBlockedError(url) {
  return { previewBlocked: true, url: pathOf(url) }
}

export function isPreviewBlockedError(error) {
  return !!error && typeof error === 'object' && error.previewBlocked === true
}

/** 这份请求配置带着的预览码（没带 → null） */
export function previewTokenOfConfig(config) {
  const headers = config && config.headers
  const token = headers && headers[PREVIEW_MENU_HEADER]
  return typeof token === 'string' && token ? token : null
}
